import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppFooter } from "@/components/layout/AppShell";
import { SkedMark } from "@/components/SkedMark";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { resubscribeDigestViaToken } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";

// Public page shown after GET /digest/unsubscribe flips the digest off.
// Reads ?property= and ?email= out of the redirect so the confirmation
// can render the specific property + masked email without another API
// call. ?error= covers the expired / malformed / not_found branches.

function errorCopy(error: string): { title: string; body: string } {
  if (error === "expired") {
    return {
      title: "This unsubscribe link has expired",
      body: "Unsubscribe links are valid for 90 days. Head to Settings in Traffic Snapshot to turn the weekly digest off from there.",
    };
  }
  if (error === "not_found") {
    return {
      title: "We couldn't find that subscription",
      body: "The subscription may have already been removed. If you still have digest emails arriving, open Traffic Snapshot and toggle the weekly digest off in Settings.",
    };
  }
  return {
    title: "We couldn't verify this unsubscribe link",
    body: "The link looks malformed or tampered with. Open Traffic Snapshot and turn the weekly digest off from Settings instead.",
  };
}

export function UnsubscribePage() {
  usePageMeta({ title: "Unsubscribe · Traffic Snapshot" });
  const [params] = useSearchParams();
  const error = params.get("error");
  const token = params.get("token") ?? "";
  const propertyName = params.get("property") || "your GA4 property";
  const email = params.get("email") || "your inbox";

  const [busy, setBusy] = useState(false);
  const [undoState, setUndoState] = useState<"idle" | "done" | "failed">("idle");
  const [undoError, setUndoError] = useState<string | null>(null);

  async function handleUndo() {
    if (!token) {
      setUndoState("failed");
      setUndoError("Missing token — we can't re-enable without it. Open Settings to re-subscribe.");
      return;
    }
    setBusy(true);
    setUndoError(null);
    try {
      await resubscribeDigestViaToken(token);
      setUndoState("done");
    } catch (err) {
      setUndoState("failed");
      setUndoError(err instanceof Error ? err.message : "Re-subscribe failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-dvh flex-col bg-peach text-ink antialiased">
      <header className="px-8 pt-8">
        <div className="mx-auto flex max-w-[1200px] items-center">
          <Link to="/" className="flex items-center gap-3 text-ink no-underline">
            <SkedMark className="h-10 w-10" aria-hidden="true" />
            <span className="leading-[1.05]">
              <span className="block text-xs font-bold tracking-tight text-neutral-500">
                Sked Social
              </span>
              <span className="block font-display text-lg font-[900] tracking-tight text-ink">
                Traffic Snapshot
              </span>
            </span>
          </Link>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <div className="rounded-[20px] border border-neutral-950/5 bg-white p-8 shadow-lg md:p-10">
            <SkedMark aria-hidden="true" className="mb-5 size-12" />

            {error ? (
              <>
                {(() => {
                  const copy = errorCopy(error);
                  return (
                    <>
                      <h1 className="font-display text-[32px] font-[900] leading-[1.1] tracking-[-0.02em] text-ink text-balance">
                        {copy.title}
                      </h1>
                      <p className="mt-4 max-w-[52ch] text-[15px] leading-[1.55] text-neutral-700 text-pretty">
                        {copy.body}
                      </p>
                    </>
                  );
                })()}
                <Link to="/app/settings" className="ts-link mt-6 inline-flex">
                  Open Settings <span className="ts-link-arrow">→</span>
                </Link>
              </>
            ) : undoState === "done" ? (
              <>
                <h1 className="font-display text-[32px] font-[900] leading-[1.1] tracking-[-0.02em] text-ink text-balance">
                  You're back on the list.
                </h1>
                <p className="mt-4 max-w-[52ch] text-[15px] leading-[1.55] text-neutral-700 text-pretty">
                  The weekly Traffic Snapshot digest for{" "}
                  <strong className="text-ink">{propertyName}</strong> will resume next Monday.
                </p>
                <Link to="/app/settings" className="ts-link mt-6 inline-flex">
                  Adjust in Settings <span className="ts-link-arrow">→</span>
                </Link>
              </>
            ) : (
              <>
                <span className="mb-[14px] inline-block rounded-full border border-pink-200 px-[14px] py-[7px] text-[11px] font-bold uppercase tracking-[0.22em] text-pink-600">
                  Unsubscribed
                </span>
                <h1 className="mt-3 font-display text-[32px] font-[900] leading-[1.1] tracking-[-0.02em] text-ink text-balance">
                  You're off the{" "}
                  <span className="font-serif italic font-semibold tracking-[-0.01em] text-purple-500">
                    weekly digest
                  </span>
                  .
                </h1>
                <p className="mt-4 max-w-[52ch] text-[15px] leading-[1.55] text-neutral-700 text-pretty">
                  We won't send any more weekly Traffic Snapshot emails to{" "}
                  <strong className="text-ink">{email}</strong> for{" "}
                  <strong className="text-ink">{propertyName}</strong>.
                </p>

                {undoError ? (
                  <Alert variant="destructive" className="mt-6">
                    <AlertTitle>Couldn't re-subscribe</AlertTitle>
                    <AlertDescription>{undoError}</AlertDescription>
                  </Alert>
                ) : null}

                <div className="mt-6 flex flex-col gap-2">
                  <p className="text-xs text-neutral-500">Accidentally unsubscribed?</p>
                  <div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleUndo}
                      disabled={busy || !token}
                    >
                      {busy ? "Re-enabling…" : "Undo — keep sending me the digest"}
                    </Button>
                  </div>
                </div>

                <p className="mt-6 max-w-[56ch] text-xs leading-[1.5] text-neutral-500 text-pretty">
                  To re-subscribe later, open Traffic Snapshot Settings and flip the Weekly digest
                  toggle back on.
                </p>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-sm">
            <Link to="/" className="ts-link">
              Back to Traffic Snapshot <span className="ts-link-arrow">→</span>
            </Link>
          </p>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
